import React, { useEffect } from 'react';

import styled from '@emotion/styled';

import { EventSchema } from '@root/resources/wt/types';

import { useTrackFunnelEvents } from '@utils/hooks/funnel_events/use_track_funnel_event';
import { ProtectionPlanEnum } from '@utils/protection_plan';

import {
  CardPadded,
  HelpText,
  Pill,
  Question,
  SelectRow,
  SelectStack,
} from '../shared';
import { CHALLENGER as T } from '../tokens';

/** Figma 1:3209 — H1, help, and selector titles/descriptions. */
const OPTIONS = [
  {
    value: ProtectionPlanEnum.MINIMAL_COVERAGE,
    title: 'Weight-based $1 per pound',
    description: 'Coverage for each item of $1 per pound for any incidents.',
    price: 'Included',
  },
  {
    value: ProtectionPlanEnum.PROTECTION_1000,
    title: '$1,000 of Protection',
    description:
      'Coverage for any incidents to all of your items, including loss or damage.',
    price: '$15/mo',
  },
  {
    value: ProtectionPlanEnum.PROTECTION_2500,
    title: '$2,500 of Protection',
    description:
      'Coverage for any incidents to all of your items, including loss or damage.',
    price: '$25/mo',
  },
] as const;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[8]};
  margin-bottom: ${T.space[24]};
`;

type Props = {
  value?: ProtectionPlanEnum;
  onChange: (plan: ProtectionPlanEnum) => void;
};

export const ProtectionSection: React.FC<Props> = ({ value, onChange }) => {
  const trackFunnelEvent = useTrackFunnelEvents();

  useEffect(() => {
    trackFunnelEvent({
      schema: EventSchema.WWW__ProtectionPlanViewed,
      action: 'display',
      metadata: {},
    });
  }, [trackFunnelEvent]);

  const handleChange = (plan: ProtectionPlanEnum) => {
    trackFunnelEvent({
      schema: EventSchema.WWW__ProtectionPlanCompleted,
      action: 'submit',
      metadata: {
        protection_plan_slug:
          plan === ProtectionPlanEnum.MINIMAL_COVERAGE ? undefined : plan,
      },
    });
    onChange(plan);
  };

  return (
    <CardPadded>
      <Header>
        <Question>What level of protection do you want?</Question>
        <HelpText>
          While your items are stored at our secure warehouse facility with 24/7
          monitoring, accidents can happen. Choose the level of coverage that
          best fits your needs.
        </HelpText>
      </Header>
      <SelectStack>
        {OPTIONS.map((option) => (
          <SelectRow
            key={option.value}
            selected={value === option.value}
            onClick={() => handleChange(option.value)}
            title={option.title}
            description={option.description}
            price={option.price}
            badge={
              option.value === ProtectionPlanEnum.PROTECTION_1000 ? (
                <Pill>Most popular</Pill>
              ) : undefined
            }
          />
        ))}
      </SelectStack>
    </CardPadded>
  );
};
